/**
 * HTTPS Enforcer - SSL/TLS Security Utility
 * Addresses CWE-327 by enforcing secure connections and protocols
 */

class HTTPSEnforcer {
  static isProduction = process.env.NODE_ENV === 'production';
  static isHTTPS = window.location.protocol === 'https:';

  /**
   * Enforce HTTPS connections in production
   * Redirects HTTP to HTTPS to prevent downgrade attacks
   */
  static enforceHTTPS() {
    if (this.isProduction && !this.isHTTPS) {
      console.warn('🔒 Redirecting to HTTPS for security');
      const httpsUrl = `https:${window.location.href.substring(window.location.protocol.length)}`;
      window.location.replace(httpsUrl);
      return false; // Connection upgraded
    }
    return true; // Already secure or development
  }

  /**
   * Configure secure defaults for fetch API
   * Ensures all requests use secure protocols and headers
   */
  static configureSecureDefaults() {
    // Store original fetch for restoration if needed
    if (!window._originalFetch) {
      window._originalFetch = window.fetch;
    }

    // Override fetch with security enhancements
    window.fetch = (url, options = {}) => {
      const secureOptions = {
        ...options,
        mode: options.mode || 'cors',
        credentials: options.credentials || 'same-origin',
        referrerPolicy: 'strict-origin-when-cross-origin',
        headers: {
          // Security headers for requests
          'Strict-Transport-Security': 'max-age=31536000; includeSubDomains',
          'X-Content-Type-Options': 'nosniff',
          'X-Frame-Options': 'DENY',
          'Referrer-Policy': 'strict-origin-when-cross-origin',
          ...options.headers,
        },
      };

      // Ensure HTTPS URLs in production
      if (this.isProduction && typeof url === 'string' && url.startsWith('http://')) {
        url = url.replace('http://', 'https://');
        console.warn('🔒 Upgraded HTTP URL to HTTPS:', url);
      }

      return window._originalFetch(url, secureOptions);
    };
  }

  /**
   * Validate current connection security
   * Checks for secure protocol and security headers
   */
  static validateConnectionSecurity() {
    const securityReport = {
      isSecure: false,
      protocol: window.location.protocol,
      issues: [],
      recommendations: [],
    };

    // Check protocol
    if (!this.isHTTPS) {
      securityReport.issues.push('Connection not using HTTPS');
      securityReport.recommendations.push('Upgrade to HTTPS');
    } else {
      securityReport.isSecure = true;
    }

    // Check for mixed content
    if (this.isHTTPS && this.hasMixedContent()) {
      securityReport.issues.push('Mixed content detected');
      securityReport.recommendations.push('Ensure all resources use HTTPS');
    }

    // Check Content Security Policy
    if (!this.hasCSP()) {
      securityReport.issues.push('Content Security Policy not detected');
      securityReport.recommendations.push('Implement CSP headers');
    }

    return securityReport;
  }

  /**
   * Check for mixed content (HTTP resources on HTTPS page)
   */
  static hasMixedContent() {
    const resources = [
      ...Array.from(document.scripts),
      ...Array.from(document.stylesheets || []),
      ...Array.from(document.images),
      ...Array.from(document.querySelectorAll('link[href]')),
    ];

    return resources.some((resource) => {
      const src = resource.src || resource.href;
      return src && src.startsWith('http://');
    });
  }

  /**
   * Check if Content Security Policy is implemented
   */
  static hasCSP() {
    const metaCSP = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
    return metaCSP !== null;
  }

  /**
   * Initialize SSL/TLS security measures
   * Call this early in application startup
   */
  static initialize() {
    console.log('🔒 Initializing SSL/TLS security measures');

    // Enforce HTTPS redirect
    const isSecure = this.enforceHTTPS();
    if (!isSecure) return; // Redirecting, don't continue initialization

    // Configure secure fetch defaults
    this.configureSecureDefaults();

    // Set up security monitoring
    this.setupSecurityMonitoring();

    // Validate current security
    const securityReport = this.validateConnectionSecurity();

    if (securityReport.isSecure) {
      console.log('✅ SSL/TLS security initialized successfully');
    } else {
      console.warn('⚠️ Security issues detected:', securityReport.issues);
    }

    return securityReport;
  }

  /**
   * Set up security monitoring and reporting
   */
  static setupSecurityMonitoring() {
    // Monitor for CSP violations
    document.addEventListener('securitypolicyviolation', (event) => {
      console.warn('🚨 CSP Violation:', {
        blockedURI: event.blockedURI,
        violatedDirective: event.violatedDirective,
        originalPolicy: event.originalPolicy,
      });

      // Report to security monitoring (implement as needed)
      this.reportSecurityEvent('csp_violation', {
        blockedURI: event.blockedURI,
        directive: event.violatedDirective,
      });
    });

    // Monitor for mixed content
    if (this.isHTTPS) {
      this.monitorMixedContent();
    }
  }

  /**
   * Monitor for mixed content issues
   */
  static monitorMixedContent() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node;
            const src = element.src || element.href;

            if (src && src.startsWith('http://')) {
              console.warn('🚨 Mixed content detected:', src);
              this.reportSecurityEvent('mixed_content', { src });
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Report security events (implement according to your monitoring system)
   */
  static reportSecurityEvent(eventType, details) {
    // Implement reporting to your security monitoring system
    console.log(`🔍 Security Event [${eventType}]:`, details);

    // Example: Send to analytics or monitoring service
    // analytics.track('security_event', { type: eventType, ...details });
  }

  /**
   * Get current security status
   */
  static getSecurityStatus() {
    return {
      timestamp: new Date().toISOString(),
      protocol: window.location.protocol,
      isHTTPS: this.isHTTPS,
      isProduction: this.isProduction,
      userAgent: navigator.userAgent,
      hasMixedContent: this.isHTTPS ? this.hasMixedContent() : null,
      hasCSP: this.hasCSP(),
      securityReport: this.validateConnectionSecurity(),
    };
  }

  /**
   * Restore original fetch if needed
   */
  static restoreOriginalFetch() {
    if (window._originalFetch) {
      window.fetch = window._originalFetch;
      delete window._originalFetch;
      console.log('🔓 Original fetch restored');
    }
  }
}

// Auto-initialize in production
if (HTTPSEnforcer.isProduction) {
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      HTTPSEnforcer.initialize();
    });
  } else {
    HTTPSEnforcer.initialize();
  }
}

export default HTTPSEnforcer;
